'use client';

import { useMemo, useEffect, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  stripEncountersOptions,
  resolveEncountersMetricCode,
} from 'src/utils/strategyHelpers';

export interface HeatmapMetricSource {
  id: number;
  label: string;
  execution_params?: Record<string, any> | null;
  display_options: Array<{ code: string; label: string; type: string }>;
}

export interface FlatHeatmapMetric {
  code: string;
  label: string;
  type: string;
  metricLabel: string;
  metricId: number;
}

export interface GroupedHeatmapMetric {
  label: string;
  options: FlatHeatmapMetric[];
}

interface UseHeatmapMetricsParams {
  metrics: HeatmapMetricSource[] | undefined;
  selectedMetric: { code: string; label: string } | null;
  onMetricChange: (metric: { code: string; label: string } | null) => void;
}

/**
 * Shared hook for processing heatmap metrics: flattening, grouping,
 * encounters flag alignment, and metric code resolution.
 * Used by Discovery HeatmapMetricPopover. Strategy HeatmapControls can
 * adopt this hook to eliminate its duplicate logic in a follow-up.
 */
const useHeatmapMetrics = ({
  metrics,
  selectedMetric,
  onMetricChange,
}: UseHeatmapMetricsParams) => {
  const { showEncountersTotals } = useFlags();

  // Flatten metrics with direction labels
  const allFlatMetrics: FlatHeatmapMetric[] = useMemo(
    () =>
      metrics?.flatMap((metric) => {
        const direction = metric.execution_params?.direction;
        const displayLabel = direction
          ? `${metric.label} (${direction})`
          : metric.label;

        return metric.display_options.map((option) => ({
          ...option,
          metricLabel: displayLabel,
          metricId: metric.id,
        }));
      }) || [],
    [metrics]
  );

  // All option codes for encounters swap logic
  const allOptionCodes = useMemo(
    () =>
      (metrics ?? []).flatMap((metric) =>
        metric.display_options.map((option) => option.code)
      ),
    [metrics]
  );

  // Strip standalone encounters options
  const flatMetrics = useMemo(
    () => stripEncountersOptions<FlatHeatmapMetric>(allFlatMetrics),
    [allFlatMetrics]
  );

  // Resolve to encounters sibling when flag is on
  const requestCodeFor = useCallback(
    (code: string) =>
      resolveEncountersMetricCode(code, allOptionCodes, !!showEncountersTotals),
    [allOptionCodes, showEncountersTotals]
  );

  // Group metrics by their id
  const groupedItems = useMemo(() => {
    const groups: Record<number, GroupedHeatmapMetric> = {};
    flatMetrics.forEach((item) => {
      const key = item.metricId;
      if (!groups[key]) {
        groups[key] = { label: item.metricLabel, options: [] };
      }
      groups[key].options.push(item);
    });
    return groups;
  }, [flatMetrics]);

  // Keep selected metric aligned with encounters flag changes
  useEffect(() => {
    const current = selectedMetric?.code;
    if (!current) return;
    let desired = current;
    if (showEncountersTotals) {
      desired =
        resolveEncountersMetricCode(current, allOptionCodes, true) ?? current;
    } else if (current.endsWith('_encounters')) {
      const claims = current.replace(/_encounters$/, '_claims');
      if (allOptionCodes.includes(claims)) desired = claims;
    }
    if (desired !== current) {
      onMetricChange({ code: desired, label: selectedMetric?.label });
    }
  }, [
    showEncountersTotals,
    allOptionCodes,
    selectedMetric?.code,
    selectedMetric?.label,
    onMetricChange,
  ]);

  return {
    flatMetrics,
    groupedItems,
    requestCodeFor,
  };
};

export default useHeatmapMetrics;
